import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BadgeColors,
  BadgeComponent,
  ButtonComponent,
  CardComponent,
  DidDocumentModalComponent,
  IconButtonComponent,
  SlidingSideViewComponent,
  ToasterService,
  TooltipComponent,
} from 'shared';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';

import { CompanyService } from '../../../../core/domain/company/company.service';
import { CountriesService } from '../../../../core/domain/countries/countries.service';
import { getCountryFlagUrlByNumeric } from '../../../../core/utils/country-flag.util';
import { CompanyDto } from '../../../api/model/companyDto';
import { CredentialJsonModalComponent } from './credential-json-modal/credential-json-modal.component';

// Map from credential type name (in VC) to the string value used in the API
const CREDENTIAL_TYPE_MAP: Record<string, string> = {
  GBAFacilityReportIssuer: CompanyDto.RequestedCredentialTypesEnum.GbaFacilityReportIssuer,
  GBAVerifiedCompany: CompanyDto.RequestedCredentialTypesEnum.GbaVerifiedCompany,
  GBAApprovedVerifierCredential: CompanyDto.RequestedCredentialTypesEnum.GbaApprovedVerifierCredential,
  GBAApprovedCertifierCredential: CompanyDto.RequestedCredentialTypesEnum.GbaApprovedCertifierCredential,
  GBAApprovedCarbonCalculationPartnerCredential: CompanyDto.RequestedCredentialTypesEnum.GbaApprovedCarbonCalculationPartnerCredential,
};

export interface UnifiedCredential {
  type: string;
  label: string;
  status: 'active' | 'expired' | 'pending' | 'revoked';
  issuedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  isIssued: boolean;
  rawCredential?: any;
}

@Component({
  selector: 'app-company-details-view',
  imports: [
    TranslateModule,
    SlidingSideViewComponent,
    ButtonComponent,
    CardComponent,
    BadgeComponent,
    DidDocumentModalComponent,
    CredentialJsonModalComponent,
    IconButtonComponent,
    TooltipComponent,
  ],
  templateUrl: './company-details-view.component.html',
  styleUrl: './company-details-view.component.scss',
})
export class CompanyDetailsViewComponent {
  companyDid = input.required<string>();
  isOpen = input<boolean>(false);

  closed = output<void>();
  credentialRevoked = output<{ companyDid: string; credential: any }>();

  private readonly companyService = inject(CompanyService);
  private readonly countriesService = inject(CountriesService);
  private readonly toasterService = inject(ToasterService);
  private readonly translate = inject(TranslateService);

  company = signal<CompanyDto | null>(null);
  credentials = signal<any[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);

  didDocument = signal<any>(null);
  isDidModalOpen = signal(false);
  isCredentialModalOpen = signal(false);
  selectedCredential = signal<any>(null);
  issuedDateMode = signal<'absolute' | 'relative'>('absolute');
  expiresDateMode = signal<'relative' | 'absolute'>('relative');
  revokedCredentials = signal<Map<string, string>>(new Map());

  isActive = computed(() => {
    const status = this.company()?.status;
    return status === 'approved';
  });

  // Unified credentials: merge issued credentials with requested types
  unifiedCredentials = computed((): UnifiedCredential[] => {
    const issuedCredentials = this.credentials();
    const company = this.company();
    const requestedTypes = company?.requestedCredentialTypes || [];

    // Build set of issued credential types for quick lookup
    const issuedTypeSet = new Set<string>();
    const result: UnifiedCredential[] = [];

    // Add issued credentials
    for (const cred of issuedCredentials) {
      const typeName = this.getCredentialTypeName(cred);
      const enumType = CREDENTIAL_TYPE_MAP[typeName];
      if (enumType) {
        issuedTypeSet.add(enumType);
      }

      const issuedAt = cred.issuanceDate || cred.validFrom;
      const expiresAt = cred.expirationDate || cred.validUntil;
      const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false;
      const credId = cred.id || cred.proof?.jwt;
      const revokedAt = credId ? this.revokedCredentials().get(credId) : undefined;
      const isRevoked = !!revokedAt;

      result.push({
        type: typeName,
        label: typeName,
        status: isRevoked ? 'revoked' : isExpired ? 'expired' : 'active',
        issuedAt,
        expiresAt,
        revokedAt,
        isIssued: true,
        rawCredential: cred,
      });
    }

    // Add requested types that aren't issued yet
    for (const reqType of requestedTypes) {
      if (!issuedTypeSet.has(reqType)) {
        result.push({
          type: reqType,
          label: reqType,
          status: 'pending',
          isIssued: false,
        });
      }
    }

    return result;
  });

  statusBadgeColor = computed((): BadgeColors => {
    const status = this.company()?.status;
    const colorMap: Record<string, BadgeColors> = {
      approved: BadgeColors.green,
      pending: BadgeColors.yellow,
      rejected: BadgeColors.red,
      revoked: BadgeColors.grey,
    };
    return colorMap[status || 'pending'] || BadgeColors.grey;
  });

  constructor() {
    effect(() => {
      if (this.isOpen() && this.companyDid()) {
        this.fetchCompanyDetails();
      }
    });
  }

  private fetchCompanyDetails(): void {
    this.loading.set(true);
    this.error.set(null);

    const did = this.companyDid();

    this.companyService.getCompanyByDid(did).subscribe({
      next: (company) => {
        this.company.set(company);
        this.fetchCredentials(did);
        this.fetchDidDocument(company);
      },
      error: (err) => {
        console.error('Error fetching company details:', err);
        this.error.set('Failed to load company details');
        this.loading.set(false);
      },
    });
  }

  private fetchDidDocument(company: CompanyDto): void {
    if (!company.did) {
      return;
    }

    this.companyService.getCompanyDidDocument(company.did).subscribe({
      next: (didDoc: Record<string, any>) => {
        this.didDocument.set(didDoc);
      },
      error: (err: unknown) => {
        console.error('Error fetching DID document:', err);
      },
    });
  }

  private fetchCredentials(did: string): void {
    this.companyService.getCompanyCredentials(did).subscribe({
      next: (response) => {
        const vp = response.verifiablePresentation;
        const credentials = vp?.['verifiableCredential'] || [];

        const credentialsList = Array.isArray(credentials) ? credentials : [credentials];

        const uniqueCredentials = credentialsList.filter(
          (credential: any, index: number, self: any[]) => {
            return (
              index ===
              self.findIndex(
                (c: any) =>
                  (c.id && credential.id && c.id === credential.id) ||
                  JSON.stringify(c) === JSON.stringify(credential),
              )
            );
          },
        );

        this.credentials.set(uniqueCredentials);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error fetching credentials:', err);
        this.credentials.set([]);
        this.loading.set(false);
      },
    });
  }

  onClose(): void {
    this.closed.emit();
  }

  copyToClipboard(text: string, label: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toasterService.info({
        message: this.translate.instant('copied-to-clipboard', { label }),
        autoHide: true,
      });
    });
  }

  getCredentialTypeName(credential: any): string {
    if (!credential?.type) return 'Unknown';

    // Get the specific type (not VerifiableCredential)
    const types = Array.isArray(credential.type) ? credential.type : [credential.type];
    const specificType = types.find((t: string) => t !== 'VerifiableCredential');

    return specificType || 'VerifiableCredential';
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  }

  toggleDateDisplay(field: 'issued' | 'expires'): void {
    if (field === 'issued') {
      this.issuedDateMode.update((m) => (m === 'absolute' ? 'relative' : 'absolute'));
    } else {
      this.expiresDateMode.update((m) => (m === 'relative' ? 'absolute' : 'relative'));
    }
  }

  getDaysSince(dateString: string): string {
    if (!dateString) return 'N/A';
    const now = new Date();
    const issued = new Date(dateString);
    const diffTime = now.getTime() - issued.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  }

  getDaysRemaining(dateString: string): string {
    if (!dateString) return 'N/A';
    const now = new Date();
    const expiry = new Date(dateString);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `${Math.abs(diffDays)} days ago`;
    } else if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return '1 day';
    } else {
      return `${diffDays} days`;
    }
  }

  openDidModal(): void {
    this.isDidModalOpen.set(true);
  }

  closeDidModal(): void {
    this.isDidModalOpen.set(false);
  }

  openCredentialModal(credential: any): void {
    this.selectedCredential.set(credential);
    this.isCredentialModalOpen.set(true);
  }

  closeCredentialModal(): void {
    this.isCredentialModalOpen.set(false);
    this.selectedCredential.set(null);
  }

  getCredentialStatusBadgeColor(status: UnifiedCredential['status']): BadgeColors {
    const colorMap: Record<UnifiedCredential['status'], BadgeColors> = {
      active: BadgeColors.green,
      expired: BadgeColors.red,
      pending: BadgeColors.yellow,
      revoked: BadgeColors.red,
    };
    return colorMap[status] || BadgeColors.grey;
  }

  getCredentialStatusLabel(status: UnifiedCredential['status']): string {
    const labelMap: Record<UnifiedCredential['status'], string> = {
      active: 'Active',
      expired: 'Expired',
      pending: 'Pending',
      revoked: 'Revoked',
    };
    return labelMap[status] || status;
  }

  canRevokeCredential(credential: UnifiedCredential): boolean {
    return this.company()?.status === 'approved' && credential.isIssued && credential.status === 'active';
  }

  markCredentialRevoked(credentialId: string, revokedAt: string): void {
    this.revokedCredentials.update((map) => new Map(map).set(credentialId, revokedAt));
  }

  requestRevokeCredential(credential: any): void {
    const company = this.company();
    if (!company) return;

    this.credentialRevoked.emit({ companyDid: company.did, credential });
  }

  getFlagUrl(countryIso2: string | undefined): string | null {
    if (!countryIso2) return null;
    const country = this.countriesService.getCountryByIso2(countryIso2);
    if (!country) return null;
    return getCountryFlagUrlByNumeric(country.codeISONumeric);
  }
}
